import * as React from "react";
import {
  List,
  Datagrid,
  TextField,
  TopToolbar,
  CreateButton,
  DeleteButton,
  EditButton,
  FunctionField,
  usePermissions,
  useListContext,
  useRedirect,
} from "react-admin";
import { useClub } from "../club/ClubContext";
import { can } from "../auth/can";
import { TeamAssignmentButton } from "./TeamAssignmentButton";
import { useMediaQuery, Card, CardContent, Stack, Typography, IconButton, Tooltip, Box, Chip, Avatar } from "@mui/material";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import DeleteIcon from '@mui/icons-material/Delete';

const displayMemberName = (member: any) =>
  member.display_name ||
  `${member.first_name ?? ""} ${member.last_name ?? ""}`.trim() ||
  member.email ||
  "Unbekannt";

const initials = (member: any) =>
  displayMemberName(member)
    .split(" ")
    .map((part: string) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const makeAvatarUrl = (url?: string | null) => {
  if (!url) return undefined;
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_CDN_URL ?? ""}/static/avatars/${url}`;
};

function TeamMembers({ members }: { members?: any[] }) {
  const list = members ?? [];
  if (!list.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Keine Spieler zugeordnet
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
      {list.map((member) => (
        <Chip
          key={member.id}
          size="small"
          avatar={<Avatar src={makeAvatarUrl(member.avatar_url)}>{initials(member)}</Avatar>}
          label={displayMemberName(member)}
          variant="outlined"
          sx={{ maxWidth: "100%", "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" } }}
        />
      ))}
    </Stack>
  );
}

// ---------- Actions oben (Create) ----------
const ListActions = () => {
  const { permissions, isLoading } = usePermissions();
  if (isLoading) return null;

  return (
    <TopToolbar>
      {can(permissions, "teams", "create") && <CreateButton label="Hinzufügen" />}
    </TopToolbar>
  );
};

// ---------- Mobile Card List ----------
function TeamMobileCards() {
  const { data, isLoading } = useListContext<any>();
  const { permissions, isLoading: permsLoading } = usePermissions();
  const redirect = useRedirect();

  if (isLoading || permsLoading) return null;

  const allowEdit = can(permissions, "teams", "edit");
  const allowDelete = can(permissions, "teams", "delete");
  const allowAssign = can(permissions, "teams", "assign");

  const onAssign = (id: any) => {
    // Deine Route ist custom: /team-assignments?teamId=...
    redirect(`/team-assignments?teamId=${id}`);
  };

  return (
    <Stack spacing={1} sx={{ p: 1 }}>
      {(data ?? []).map((record: any) => (
        <Card
          key={record.id}
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} noWrap>
                  {record.name}
                </Typography>
                <Box sx={{ mt: 0.75 }}>
                  <TeamMembers members={record.members} />
                </Box>
                {/* optional: sekundäre Infos */}
                {/* <Typography variant="body2" color="text.secondary" noWrap>...</Typography> */}
              </Box>

              <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                {allowAssign && (
                  <Tooltip title="Team-Zuordnung">
                    <IconButton size="small" onClick={() => onAssign(record.id)}>
                      <GroupAddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                {allowEdit && (
                  <Tooltip title="Bearbeiten">
                    <IconButton
                      size="small"
                      onClick={() => redirect("edit", "teams", record.id)}
                    >
                      {/* MUI Icon nicht zwingend, wir nutzen nur Button-Optik */}
                       <ModeEditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                {allowDelete && (
                  // React-Admin DeleteButton braucht recordContext,
                  // daher für mobile lieber direkt in Show/Edit lassen ODER Custom Mutation.
                  // Minimal: wir schicken auf show, dort gibts Delete actions.
                  <Tooltip title="Löschen">
                    <IconButton
                      size="small"
                      onClick={() => redirect("show", "teams", record.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

// ---------- TeamList ----------
export function TeamList() {
  const { clubId } = useClub();
  const isSmall = useMediaQuery((theme: any) => theme.breakpoints.down("sm"));

  const { permissions, isLoading } = usePermissions();
  if (isLoading) return null;

  const allowEdit = can(permissions, "teams", "edit");
  const allowDelete = can(permissions, "teams", "delete");

  return (
      <>
       <Stack spacing={2} sx={{ p: 2, width: "100%", }}>
       <Typography variant="h5" fontWeight={700}>
        Teams
      </Typography>
    <List
      filter={{ clubId }}
      actions={<ListActions />}
      queryOptions={{ enabled: !!clubId }}
    >
      {isSmall ? (
        <TeamMobileCards />
      ) : (
        <Datagrid
          rowClick={false}
          bulkActionButtons={false}
        >
          <TextField source="name" />
          <FunctionField
            label="Spieler"
            render={(record: any) => <TeamMembers members={record.members} />}
          />
          {/* Desktop: dein existierender record-aware Button */}
          <TeamAssignmentButton />
          {allowEdit && <EditButton label="" />}
          {allowDelete && <DeleteButton label="" />}
        </Datagrid>
      )}
    </List>
    </Stack>
    </>
  );
}
